// One-line human summaries for the scout run's "How this was computed"
// step list. Pure transcript → text: each tool child of a scout chat
// becomes { name, summary, rows, error }, derived only from what the
// transcript persists (args, details, status), so a rehydrated run
// summarizes identically to a live one. Kept out of ChatConsole so the
// derivation is unit-testable.

import { coerceJsonArg } from "./coerceJsonArg.js";

// "ENTITY_TYPE_ILO_CLUSTER" → "ilo_cluster"
function entityLabel(entity) {
    if (typeof entity !== "string" || !entity) return null;
    return entity.replace(/^ENTITY_TYPE_/, "").toLowerCase();
}

// Output dimension/metric → its short label. Prefer the model-chosen
// outputAlias; fall back to the raw field name minus its enum prefix.
function outputLabel(o) {
    if (typeof o?.outputAlias === "string" && o.outputAlias) return o.outputAlias;
    const raw = o?.field?.field ?? o?.computed?.field?.field ?? o?.field;
    return typeof raw === "string" ? raw.replace(/^DIMENSION_FIELD_/, "").toLowerCase() : null;
}

function labelList(items, cap = 3) {
    const labels = (Array.isArray(items) ? items : []).map(outputLabel).filter(Boolean);
    if (!labels.length) return null;
    const extra = labels.length - cap;
    return labels.slice(0, cap).join(", ") + (extra > 0 ? ` +${extra}` : "");
}

function rangeLabel(timeRange) {
    const secs = Number(timeRange?.relative?.durationSeconds);
    if (Number.isFinite(secs) && secs > 0) {
        if (secs % 86400 === 0) return `${secs / 86400}d`;
        if (secs % 3600 === 0) return `${secs / 3600}h`;
        return `${secs}s`;
    }
    return timeRange ? "custom window" : null;
}

// Summarize one analytics-graph Query spec (the dialect the chat agent
// writes: { root: {entity, children}, timeRange, filter, output:
// { dimensions, metrics }, orderBy, limit }).
function summarizeAnalyticsSpec(spec) {
    if (!spec || typeof spec !== "object") return "";
    const parts = [];
    const entities = [
        entityLabel(spec.root?.entity),
        ...(Array.isArray(spec.root?.children) ? spec.root.children : []).map((c) => entityLabel(c?.entity)),
    ].filter(Boolean);
    if (entities.length) parts.push(entities.join(" + "));
    const dims = labelList(spec.output?.dimensions);
    if (dims) parts.push(`by ${dims}`);
    const metrics = labelList(spec.output?.metrics);
    if (metrics) parts.push(`→ ${metrics}`);
    const range = rangeLabel(spec.timeRange);
    if (range) parts.push(range);
    if (spec.filter) parts.push("filtered");
    const limit = Number(spec.limit);
    if (Number.isFinite(limit) && limit > 0 && limit <= 1000) parts.push(`limit ${limit}`);
    return parts.join(" · ");
}

// First non-empty line of a python cell, clipped — enough to recognise
// the computation without dumping the whole cell into the step row.
function firstCodeLine(code) {
    if (typeof code !== "string") return "";
    const line = code.split("\n").map((l) => l.trim()).find((l) => l && !l.startsWith("#")) || "";
    return line.length > 80 ? `${line.slice(0, 79)}…` : line;
}

// Clip a narration blob to a readable note: cut at `max`, backtracking
// to a sentence end (or a word break) so the note doesn't stop mid-word.
function clipNote(text, max) {
    const t = text.trim();
    if (t.length <= max) return t;
    const slice = t.slice(0, max);
    const sentence = Math.max(slice.lastIndexOf(". "), slice.lastIndexOf(".\n"));
    if (sentence > max * 0.5) return slice.slice(0, sentence + 1);
    const word = slice.lastIndexOf(" ");
    return `${slice.slice(0, word > max * 0.5 ? word : max)}…`;
}

const ASSISTANT_NOTE_MAX = 420;
const THINKING_NOTE_MAX = 280;

// Grouped reasoning trace for the "How this was computed" panel: the
// run's own narration paired with the tool steps it precedes, so the
// panel shows WHY each query ran and what was concluded — not just the
// bare calls. For the gap before a step the LAST assistant text wins
// (it's the model's stated intent for the call); when the model wrote
// nothing, a clipped slice of its thinking stands in. Steps that follow
// with no narration of their own attach to the same group, so a
// conclusion and the hand-off calls it explains render as one block. A
// trailing assistant message (the run's wrap-up) becomes a note-only
// group; trailing thinking is dropped. Returns
// [{ note: { text, thinking } | null, steps: [summarizeScoutStep…] }].
export function buildScoutTrace(chat) {
    const groups = [];
    let pendingAssistant = null;
    let pendingThinking = null;
    const takeNote = () => {
        const note = pendingAssistant
            ? { text: clipNote(pendingAssistant, ASSISTANT_NOTE_MAX), thinking: false }
            : pendingThinking
                ? { text: clipNote(pendingThinking, THINKING_NOTE_MAX), thinking: true }
                : null;
        pendingAssistant = null;
        pendingThinking = null;
        return note;
    };
    for (const it of (chat?.items || [])) {
        if (it?.kind !== "turn") continue;
        for (const ch of (it?.children || [])) {
            if (ch?.kind === "tool" && ch?.name) {
                const note = takeNote();
                if (note || !groups.length) groups.push({ note, steps: [] });
                groups[groups.length - 1].steps.push(summarizeScoutStep(ch));
            } else if (ch?.kind === "assistant" && typeof ch?.text === "string" && ch.text.trim()) {
                pendingAssistant = ch.text;
            } else if (ch?.kind === "thinking" && typeof ch?.text === "string" && ch.text.trim()) {
                pendingThinking = ch.text;
            }
        }
    }
    if (pendingAssistant) {
        groups.push({ note: { text: clipNote(pendingAssistant, ASSISTANT_NOTE_MAX), thinking: false }, steps: [] });
    }
    return groups;
}

// One transcript tool child → a display row for the step list.
// `rows` is the result row count when the persisted details still carry
// them (they may have been evicted for storage — then null).
export function summarizeScoutStep(node) {
    const name = node?.name || "tool";
    const error = node?.status === "error";
    const args = coerceJsonArg(node?.args);
    const a = args && typeof args === "object" ? args : {};
    let summary = "";
    let rows = null;
    switch (name) {
        case "analytics_query": {
            summary = summarizeAnalyticsSpec(coerceJsonArg(a.spec));
            const r = node?.details?.rows;
            if (Array.isArray(r)) rows = r.length;
            break;
        }
        case "execute_python":
            summary = firstCodeLine(a.code);
            break;
        case "schema_search":
            summary = typeof a.query === "string" ? `"${a.query}"` : "";
            break;
        case "schema_describe_entity": {
            // The tool's parameter is `name` (see describeEntityTool).
            const target = typeof a.name === "string" ? a.name : "";
            summary = entityLabel(target) || target;
            break;
        }
        case "set_starter_cards": {
            const cards = coerceJsonArg(a.cards);
            if (Array.isArray(cards) && cards.length) {
                summary = `${cards.length} tailored starter question${cards.length === 1 ? "" : "s"}`;
            }
            break;
        }
        case "set_tailored_canvases": {
            const canvases = coerceJsonArg(a.canvases);
            if (Array.isArray(canvases) && canvases.length) {
                const titles = canvases
                    .map((c) => coerceJsonArg(c))
                    .map((c) => (typeof c?.title === "string" ? c.title.trim() : null))
                    .filter(Boolean);
                summary = `${canvases.length} canvas${canvases.length === 1 ? "" : "es"}${titles.length ? `: ${titles.slice(0, 4).join(", ")}` : ""}`;
            }
            break;
        }
        default:
            break;
    }
    return { name, summary, rows, error };
}
